import styled from '@emotion/styled';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { mediaQuery, Spacings } from '@styles/index';
import PreviewButton from '@comp/PreviewButton/PreviewButton';
import { evtNameCategoryMap } from '@constants/index';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import { segment } from '@actions/analytics';
import PlayContained from 'aquilla/icons/actions/PlayContained';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useWindowSize from '@hooks/useWindowSize';
import { getIntroVideoURL } from '@utils/textHelper';
import { BATCH_PREVIEW, BATCH_KEYS } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import Typography from 'aquilla/core/atoms/Typography';
import Tag from 'aquilla/core/atoms/Tag';
import Play from 'aquilla/icons/actions/Play';
import { getBatchTag } from '@comp/BatchCard/util';

const Preview = styled(Typography)`
  margin-left: ${Spacings.SPACING_6};
  font-weight: 700;
`;

const Intro = styled.iframe`
  height: 180px;
  width: 320px;
  border-radius: ${Spacings.SPACING_2B};
  margin-right: ${Spacings.SPACING_8B};
  background-color: var(--color-base-fill);
  transition: all 300ms ease;
`;

const Dummy = styled.div`
  position: relative;
`;

const CourseThumbnail = styled(Thumbnail)`
  position: relative;
  height: 180px;
  width: 320px;
  margin-right: ${Spacings.SPACING_8B};
  border-radius: ${Spacings.SPACING_2B};
  transition: all 300ms ease-in-out;
  ${mediaQuery.uptoMobile} {
    margin-right: 0;
    width: 100%;
    object-fit: cover;
  }
`;

const StyledPreviewButton = styled(PreviewButton)`
  background-color: var(--color-i-blue);
  border-radius: 22px;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
  :hover {
    background-color: var(--color-i-blue);
  }
`;

const TagWrapper = styled.div`
  position: absolute;
  left: ${Spacings.SPACING_4B};
  top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  gap: ${Spacings.SPACING_3B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;

const StyledTag = styled(Tag)`
  padding: 4px 11px;
  > * {
    font-size: 12px !important;
    line-height: 12px !important;
  }
`;

const ScreenWrapper = styled.div`
  &.desktop {
    display: block;
    ${mediaQuery.uptoMobile} {
      display: none;
    }
  }
  &.mobile {
    display: none;
    ${mediaQuery.uptoMobile} {
      display: block;
    }
  }
`;

const BatchThumnailV2 = ({
  batch,
  onPreview,
  thumbnailHeight,
  thumbnailWidth,
  thumbnailPriority = true,
  langLabel = false
}) => {
  const { isMobile } = useWindowSize();
  const batchGoalUID = batch?.goal?.uid;
  const { introVideo: preview, coverPhoto: thumbnail = '', languages } = batch;
  const goalSegmentData = useGoalEventProps({ batchGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batch,
    isBatch: true
  });
  const [showPreview, setShowPreview] = useState(false);
  const showIntro = () => {
    if (onPreview) onPreview();
    const lpss = segmentStorage.getItem('lpss') || 'Non Hero Batch Section';
    segment.track(
      BATCH_PREVIEW,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batch.state,
        start_date: batch.startsAt,
        end_date: batch.completedAt,
        ...(lpss && { last_primary_source_section: lpss })
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    if (lpss) segmentStorage.deleteItem('lpss');
    setShowPreview(true);
  };
  if (showPreview)
    return (
      <Intro
        className="intro-video"
        title="intro"
        src={getIntroVideoURL(preview)}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen="allowFullScreen"
      />
    );

  return (
    <Dummy>
      <CourseThumbnail
        height={thumbnailHeight || 180}
        width={thumbnailWidth || 320}
        className="course-thumbnail"
        layout={isMobile ? 'fill' : 'intrinsic'}
        imgUrl={thumbnail}
        priority={thumbnailPriority}
      >
        {langLabel && (
          <ScreenWrapper className="desktop">
            <TagWrapper>
              <StyledTag type="hollow">
                <Typography
                  as="span"
                  variant="h6"
                  color="var(--color-text-primary)"
                  style={{ fontSize: '12px', lineHeight: '12px' }}
                >
                  {languages[0].label}
                </Typography>
              </StyledTag>
              <StyledTag type="hollow">
                <Typography
                  as="span"
                  variant="h6"
                  color="var(--color-text-primary)"
                >
                  {`${getBatchTag(batch)}${batch?.syllabusTag ? ' Batch' : ''}`}
                </Typography>
              </StyledTag>
            </TagWrapper>
          </ScreenWrapper>
        )}
        {preview && (
          <>
            <ScreenWrapper className="desktop">
              <StyledPreviewButton onClick={showIntro}>
                <Play width="16px" height="16px" color="var(--color-i-white)" />
                <Preview color="var(--color-i-white)" variant="p2">
                  BATCH TRAILER
                </Preview>
              </StyledPreviewButton>
            </ScreenWrapper>
            <ScreenWrapper className="mobile">
              <StyledPreviewButton onClick={showIntro}>
                <PlayContained
                  height={Spacings.SPACING_5B}
                  width={Spacings.SPACING_5B}
                  color="var(--color-i-white)"
                />
                <Preview color="var(--color-i-white)" variant="p3">
                  BATCH TRAILER
                </Preview>
              </StyledPreviewButton>
            </ScreenWrapper>
          </>
        )}
      </CourseThumbnail>
    </Dummy>
  );
};

export default BatchThumnailV2;

BatchThumnailV2.propTypes = {
  batch: PropTypes.objectOf(PropTypes.any).isRequired
};
